// ProfessionalHomeScreen.js
// Full-featured Professional Home — live API, real stats, real jobs, search,
// notifications, interviews, skill gaps, market insights, career tips
// JobEng · professional screens

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  SafeAreaView, View, Text, StyleSheet, TouchableOpacity,
  Dimensions, ScrollView, FlatList, Alert, Image,
  TextInput, RefreshControl, ActivityIndicator,
  StatusBar, Platform, Animated, Modal,
} from 'react-native';
import {
  Ionicons, MaterialCommunityIcons, FontAwesome5,
} from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useProfessional } from '../../context/ProfessionalContext';
import { useProfessionalOnboarding } from '../../context/ProfessionalOnboardingContext';
import { apiFetch } from '../../utils/apiClient';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import useProfile from '../../hooks/useProfile';

const { width } = Dimensions.get('window');

// ─── API ─────────────────────────────────────────────────────────────────────
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

// ─── Shared storage keys (same across all professional screens) ───────────────
const PROF_KEYS = {
  TOKEN:   'prof_auth_token',
  USER:    'prof_user_data',
  PROFILE: 'prof_user_profile',
  DASH:    'prof_dashboard_data',
};

// ─── Constants ────────────────────────────────────────────────────────────────
const JOB_CATEGORIES = [
  { id: 'all',        label: 'All Jobs',    icon: 'briefcase-outline'   },
  { id: 'software',   label: 'Software',    icon: 'code-slash-outline'  },
  { id: 'mechanical', label: 'Mechanical',  icon: 'construct-outline'   },
  { id: 'electrical', label: 'Electrical',  icon: 'flash-outline'       },
  { id: 'civil',      label: 'Civil',       icon: 'business-outline'    },
  { id: 'data',       label: 'Data & AI',   icon: 'analytics-outline'   },
  { id: 'aerospace',  label: 'Aerospace',   icon: 'airplane-outline'    },
  { id: 'management', label: 'Management',  icon: 'people-outline'      },
];

const CAREER_TIPS = [
  { icon:'lightbulb', color:'#F59E0B', title:'Optimise Your Profile', text:'A complete profile gets 3x more recruiter views. Add skills, experience, and a professional photo.' },
  { icon:'bell-ring', color:'#3B82F6', title:'Set Job Alerts',       text:'Never miss an opportunity — create keyword alerts and get notified the moment a matching role is posted.' },
  { icon:'target',    color:'#10B981', title:'Quantify Achievements', text:'Bullet points with numbers (%, £, time saved) score 40% higher on ATS systems.' },
  { icon:'linkedin',  color:'#0077B5', title:'Connect on LinkedIn',   text:'Recruiters check LinkedIn 80% of the time. Make sure it matches your JobEng profile.' },
  { icon:'school',    color:'#8B5CF6', title:'Keep Learning',         text:'Adding 1–2 new certifications per year increases your salary negotiation power significantly.' },
];

const MARKET_INSIGHTS = [
  { role:'Software Engineer',    avgSalary:'£72k', demand:'High',     trend:'+12%' },
  { role:'Mechanical Engineer',  avgSalary:'£54k', demand:'Medium',   trend:'+6%'  },
  { role:'Data Engineer',        avgSalary:'£68k', demand:'Very High',trend:'+22%' },
  { role:'Civil Engineer',       avgSalary:'£48k', demand:'Medium',   trend:'+4%'  },
  { role:'DevOps Engineer',      avgSalary:'£76k', demand:'High',     trend:'+18%' },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
const greeting = () => {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
};
const fmtDate = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
};
const fmtAgo = (d) => {
  if (!d) return '';
  const m = Math.floor((Date.now() - new Date(d)) / 60000);
  if (m < 60)   return `${m}m ago`;
  if (m < 1440) return `${Math.floor(m/60)}h ago`;
  return `${Math.floor(m/1440)}d ago`;
};
const fmtSalary = (min, max) => {
  if (min && max) return `£${(+min/1000).toFixed(0)}k–£${(+max/1000).toFixed(0)}k`;
  if (min) return `From £${(+min/1000).toFixed(0)}k`;
  return 'Competitive';
};
const scoreColor = (s) => s >= 75 ? '#10B981' : s >= 50 ? '#F59E0B' : '#EF4444';
const demandColor = (d) => d === 'Very High' ? '#10B981' : d === 'High' ? '#3B82F6' : '#9CA3AF';

// ─── Component ────────────────────────────────────────────────────────────────
export default function ProfessionalHomeScreen({ navigation }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // Context
  const {
    userData, subscriptionData, profileStats: ctxStats,
    isPremiumUser, updateLastActive, getProfileCompletionTips,
  } = useProfessional();

  const {
    profileData: onboardingProfile, scoring,
  } = useProfessionalOnboarding();

  // Identity
  const [userId,       setUserId]       = useState(null);
  const [userName,     setUserName]     = useState('');
  const [userTitle,    setUserTitle]    = useState('');
  const [userLocation, setUserLocation] = useState('');
  const [profileImage, setProfileImage] = useState(null);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [profileCompletion, setProfileCompletion] = useState(0);

  // Live data
  const [jobs,               setJobs]               = useState([]);
  const [savedJobIds,        setSavedJobIds]         = useState(new Set());
  const [recentApplications, setRecentApplications] = useState([]);
  const [upcomingInterview,  setUpcomingInterview]   = useState(null);
  const [notificationCount,  setNotificationCount]   = useState(0);
  const [alertCount,         setAlertCount]          = useState(0);
  const [profileViews,       setProfileViews]        = useState(0);
  const [careerStats,        setCareerStats]         = useState({
    applications: 0, interviews: 0, offers: 0, saved: 0,
  });

  // UI
  const [isLoading,   setIsLoading]   = useState(true);
  const [refreshing,  setRefreshing]  = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selCategory, setSelCategory] = useState('all');
  const [tipIndex,    setTipIndex]    = useState(0);
  const [showSearch,  setShowSearch]  = useState(false);
  const [marketModal, setMarketModal] = useState(false);
  const [skillModal,  setSkillModal]  = useState(false);
  const [missingSkills, setMissingSkills] = useState([]);

  const fadeAnim = useRef(new Animated.Value(0)).current;
  const searchRef = useRef(null);

  // ─── Init ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    bootstrap();
  }, []);

  // Re-read shared storage every time screen is focused
  useFocusEffect(
    useCallback(() => {
      loadSharedProfile();
      updateLastActive?.();
    }, [])
  );

  // Rotate career tips every 6 seconds
  useEffect(() => {
    const t = setInterval(() => setTipIndex(i => (i + 1) % CAREER_TIPS.length), 6000);
    return () => clearInterval(t);
  }, []);

  const bootstrap = async () => {
    try {
      const uid = await AsyncStorage.getItem('userId') || await AsyncStorage.getItem('user_id');
      setUserId(uid);
      await loadSharedProfile();
      await Promise.all([
        loadJobs(),
        loadStats(uid),
        loadSavedJobs(uid),
        loadNotifications(uid),
        loadAlerts(uid),
      ]);
    } catch (e) {
      console.error('HomeScreen bootstrap:', e);
    } finally {
      setIsLoading(false);
      Animated.timing(fadeAnim, { toValue: 1, duration: 500, useNativeDriver: true }).start();
    }
  };

  // ─── Load shared storage (written by Login/Signup/Profile) ────────────────
  const loadSharedProfile = async () => {
    try {
      // Dashboard payload (quickest, most up-to-date)
      const dashCandidates = await Promise.all([
        AsyncStorage.getItem(PROF_KEYS.DASH),
        AsyncStorage.getItem('dashboardData'),
      ]);
      const dashRaw = dashCandidates.find(Boolean);
      if (dashRaw) {
        const dash = JSON.parse(dashRaw);
        const u = dash?.user || {};
        if (u.fullName)       setUserName(u.fullName);
        if (u.jobTitle)       setUserTitle(u.jobTitle);
        if (u.profileImage)   setProfileImage(u.profileImage);
        if (u.availability)   {} // available
        if (dash?.stats?.completionPercentage !== undefined) setProfileCompletion(dash.stats.completionPercentage);
        if (dash?.stats?.profileViews !== undefined) setProfileViews(dash.stats.profileViews);
      }

      // Full profile (highest fidelity — written by ProfileScreen)
      const profCandidates = await Promise.all([
        AsyncStorage.getItem(PROF_KEYS.PROFILE),
        AsyncStorage.getItem('userProfile'),
      ]);
      const profRaw = profCandidates.find(Boolean);
      if (profRaw) {
        const prof = JSON.parse(profRaw);
        const p = prof?.personal || {};
        if (p.fullName && !userName)     setUserName(p.fullName);
        if (p.jobTitle && !userTitle)    setUserTitle(p.jobTitle);
        if (p.location)                  setUserLocation(p.location);
        if (p.profileImage)              setProfileImage(p.profileImage);
        // Compute missing skills for skill gap card
        const userSkillNames = (prof?.skills || [])
          .map(s => (typeof s === 'string' ? s : (s?.name || s?.title || s?.skill_name || s?.skill || '')))
          .filter(Boolean)
          .map(s => String(s).toLowerCase());
        const missing = ['Python','CAD','Agile','SQL','Leadership','Git'].filter(
          sk => !userSkillNames.some(us => us.includes(sk.toLowerCase()))
        );
        setMissingSkills(missing.slice(0, 4));
      }

      // Raw user data (from Login)
      const userCandidates = await Promise.all([
        AsyncStorage.getItem(PROF_KEYS.USER),
        AsyncStorage.getItem('user_data'),
      ]);
      const userRaw = userCandidates.find(Boolean);
      if (userRaw) {
        const u = JSON.parse(userRaw);
        const full = [u.first_name||'', u.last_name||''].filter(Boolean).join(' ') || u.full_name || u.name || u.email || '';
        if (full && !userName)  setUserName(full);
        if (u.job_title && !userTitle) setUserTitle(u.job_title);
        if (u.location && !userLocation) setUserLocation(u.location);
        if (u.profile_image || u.avatar) setProfileImage(u.profile_image || u.avatar);
      }

      // Context fallback
      if (!userName && userData?.full_name) setUserName(userData.full_name);
      if (!userTitle && (userData?.job_title || userData?.position)) setUserTitle(userData.job_title || userData.position);
    } catch (e) {
      console.warn('loadSharedProfile:', e?.message);
    }
  };

  // ─── API loaders ──────────────────────────────────────────────────────────
  const loadJobs = async () => {
    try {
      const r = await apiFetch('/api/jobs?status=active&limit=30');
      if (!r.error && (r.data || r.jobs)) {
        setJobs(r.data || r.jobs || []);
        return;
      }
    } catch {}
    // cache fallback
    const cached = await AsyncStorage.getItem('home_cached_jobs');
    if (cached) setJobs(JSON.parse(cached));
  };

  const loadStats = async (uid) => {
    try {
      const email = userData?.email;
      if (!email && !uid) return;
      const [appsR, interR, offersR] = await Promise.allSettled([
        apiFetch(`/api/data/applications?${email ? `email=${encodeURIComponent(email)}` : `user_id=${uid}`}&limit=50`),
        apiFetch(`/api/data/interviews?${email ? `email=${encodeURIComponent(email)}` : `user_id=${uid}`}&limit=20`),
        apiFetch(`/api/recruitment/offers?${email ? `email=${encodeURIComponent(email)}` : `user_id=${uid}`}&limit=20`),
      ]);

      const apps    = appsR.status    === 'fulfilled' && !appsR.value?.error    ? (appsR.value?.data    || appsR.value    || []) : [];
      const inters  = interR.status   === 'fulfilled' && !interR.value?.error   ? (interR.value?.data   || interR.value   || []) : [];
      const offers  = offersR.status  === 'fulfilled' && !offersR.value?.error  ? (offersR.value?.data  || offersR.value  || []) : [];

      setCareerStats({
        applications: Array.isArray(apps)   ? apps.length   : 0,
        interviews:   Array.isArray(inters) ? inters.length : 0,
        offers:       Array.isArray(offers) ? offers.length : 0,
        saved:        savedJobIds.size,
      });

      // Recent applications (last 3)
      if (Array.isArray(apps) && apps.length) {
        const sorted = [...apps].sort((a,b) => new Date(b.applied_date||b.created_at||0) - new Date(a.applied_date||a.created_at||0));
        setRecentApplications(sorted.slice(0, 3));
      }

      // Upcoming interview (next one)
      if (Array.isArray(inters) && inters.length) {
        const upcoming = inters
          .filter(i => new Date(i.interview_date) > new Date() && i.status !== 'cancelled')
          .sort((a,b) => new Date(a.interview_date) - new Date(b.interview_date));
        if (upcoming.length) setUpcomingInterview(upcoming[0]);
      }

      await AsyncStorage.setItem('home_career_stats', JSON.stringify({ applications: Array.isArray(apps)?apps.length:0, interviews: Array.isArray(inters)?inters.length:0, offers: Array.isArray(offers)?offers.length:0 }));
    } catch (e) {
      // Load from cache
      const cached = await AsyncStorage.getItem('home_career_stats');
      if (cached) {
        const st = JSON.parse(cached);
        setCareerStats(p => ({ ...p, ...st }));
      }
    }
  };

  const loadSavedJobs = async (uid) => {
    try {
      const r = await apiFetch(`/api/professional/saved-jobs?${uid ? `user_id=${uid}` : ''}`);
      if (!r.error && (r.data || r.jobs)) {
        const ids = (r.data || r.jobs || []).map(j => String(j.id || j.job_id));
        setSavedJobIds(new Set(ids));
        return;
      }
    } catch {}
    const cached = await AsyncStorage.getItem(`saved_job_ids_${uid||'g'}`);
    if (cached) setSavedJobIds(new Set(JSON.parse(cached)));
  };

  const loadNotifications = async (uid) => {
    try {
      if (!uid) return;
      const r = await apiFetch(`/api/data/notifications?user_id=${uid}&unread=true&limit=50`);
      if (!r.error) setNotificationCount(Array.isArray(r) ? r.length : r?.data?.length || r?.count || 0);
    } catch {}
  };

  const loadAlerts = async (uid) => {
    try {
      if (!uid) return;
      const r = await apiFetch(`/api/professional/alerts?user_id=${uid}&active=true`);
      if (!r.error) setAlertCount((r.data || r.alerts || r || []).length || 0);
    } catch {}
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadSharedProfile();
    await Promise.all([loadJobs(), loadStats(userId), loadNotifications(userId)]);
    setRefreshing(false);
  };

  // ─── Actions ──────────────────────────────────────────────────────────────
  const toggleSaveJob = async (job) => {
    const id = String(job.id || job._id);
    const newSet = new Set(savedJobIds);
    if (newSet.has(id)) {
      newSet.delete(id);
      await apiFetch(`/api/professional/saved-jobs/${id}`, { method: 'DELETE' });
    } else {
      newSet.add(id);
      await apiFetch('/api/professional/saved-jobs', { method:'POST', body: JSON.stringify({ job_id: id }) });
    }
    setSavedJobIds(newSet);
    await AsyncStorage.setItem(`saved_job_ids_${userId||'g'}`, JSON.stringify([...newSet]));
  };

  const navigateTo = (screen, params = {}) => {
    try { navigation.navigate(screen, params); }
    catch { Alert.alert('Coming Soon', `${screen} will be available soon.`); }
  };

  const filteredJobs = jobs.filter(j => {
    if (selCategory !== 'all') {
      const txt = ((j.title||'') + (j.department||'')).toLowerCase();
      if (!txt.includes(selCategory)) return false;
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      return ((j.title||'') + (j.company||j.department||'') + (j.location||'')).toLowerCase().includes(q);
    }
    return true;
  });

  // ─── Render: Header ───────────────────────────────────────────────────────
  const renderHeader = () => (
    <View style={s.header}>
      <View style={s.headerLeft}>
        <TouchableOpacity style={s.menuBtn} onPress={() => navigation.openDrawer?.() || navigation.goBack()}>
          <Ionicons name="menu" size={22} color="#FFF" />
        </TouchableOpacity>
        <View style={s.logoRow}>
          <MaterialCommunityIcons name="briefcase" size={20} color="#FFF" />
          <Text style={s.logoText}>JobEng Pro</Text>
        </View>
      </View>
      <View style={s.headerRight}>
        <TouchableOpacity style={s.iconBtn} onPress={() => setShowSearch(v => !v)}>
          <Ionicons name="search" size={20} color="#FFF" />
        </TouchableOpacity>
        <TouchableOpacity style={s.iconBtn} onPress={() => navigateTo('Notifications')}>
          <Ionicons name="notifications-outline" size={20} color="#FFF" />
          {notificationCount > 0 && (
            <View style={s.notifBadge}><Text style={s.notifCount}>{notificationCount > 9 ? '9+' : notificationCount}</Text></View>
          )}
        </TouchableOpacity>
        <TouchableOpacity style={s.avatarBtn} onPress={() => navigateTo('Profile')}>
          {profileImage && typeof profileImage === 'string' && profileImage.startsWith('http')
            ? <Image source={{ uri: profileImage }} style={s.avatarImg} />
            : <View style={s.avatarFallback}><Text style={s.avatarInitial}>{(userName?.charAt(0) || 'P').toUpperCase()}</Text></View>
          }
        </TouchableOpacity>
      </View>
    </View>
  );

  // ─── Render: Search bar (collapsible) ────────────────────────────────────
  const renderSearch = () => showSearch && (
    <View style={s.searchBar}>
      <Ionicons name="search" size={18} color="#94a3b8" style={{ marginLeft:12 }} />
      <TextInput
        ref={searchRef}
        style={s.searchInput}
        placeholder="Search jobs, companies, skills…"
        placeholderTextColor="#94a3b8"
        value={searchQuery}
        onChangeText={setSearchQuery}
        autoFocus
      />
      {searchQuery ? (
        <TouchableOpacity onPress={() => setSearchQuery('')} style={{ padding:10 }}>
          <Ionicons name="close-circle" size={18} color="#94a3b8" />
        </TouchableOpacity>
      ) : null}
    </View>
  );

  // ─── Render: Hero card ────────────────────────────────────────────────────
  const renderHero = () => (
    <Animated.View style={[s.heroCard, { opacity: fadeAnim }]}>
      <View style={s.heroLeft}>
        <Text style={s.greetText}>{greeting()},</Text>
        <Text style={s.heroName} numberOfLines={1}>{userName || 'Professional'}</Text>
        {userTitle ? <Text style={s.heroTitle} numberOfLines={1}>{userTitle}</Text> : null}
        {userLocation ? (
          <View style={s.heroLocRow}>
            <Ionicons name="location-outline" size={13} color="rgba(255,255,255,0.7)" />
            <Text style={s.heroLoc}>{userLocation}</Text>
          </View>
        ) : null}

        {isSubscribed || isPremiumUser?.()
          ? <View style={s.premiumBadge}><MaterialCommunityIcons name="crown" size={14} color="#FFD700"/><Text style={s.premiumText}>Premium Member</Text></View>
          : Platform.OS !== 'ios'
            ? <TouchableOpacity style={s.upgradeBtn} onPress={() => navigateTo('WelcomeProfessionalScreen')}>
                <MaterialCommunityIcons name="rocket-launch" size={13} color="#FFF"/>
                <Text style={s.upgradeBtnText}>Upgrade · £14/mo</Text>
              </TouchableOpacity>
            : null
        }
      </View>

      {/* Profile completion ring */}
      <TouchableOpacity style={s.completionRing} onPress={() => navigateTo('Profile')}>
        <View style={[s.ringOuter, { borderColor: scoreColor(profileCompletion) }]}>
          <Text style={[s.ringVal, { color: scoreColor(profileCompletion) }]}>{profileCompletion}%</Text>
          <Text style={s.ringLabel}>Profile</Text>
        </View>
        <Text style={s.ringHint}>Tap to{'\n'}complete</Text>
      </TouchableOpacity>
    </Animated.View>
  );

  // ─── Render: Stats row ────────────────────────────────────────────────────
  const renderStats = () => (
    <View style={s.statsRow}>
      {[
        { label:'Applications', val: careerStats.applications, icon:'file-document-multiple-outline', color:'#3B82F6', screen:'JobApplications'     },
        { label:'Interviews',   val: careerStats.interviews,   icon:'calendar-check-outline',         color:'#10B981', screen:'InterviewDetails'     },
        { label:'Offers',       val: careerStats.offers,       icon:'handshake-outline',              color:'#F59E0B', screen:'JobsOffersScreen'     },
        { label:'Profile Views',val: profileViews,             icon:'eye-outline',                    color:'#8B5CF6', screen:'Profile'              },
      ].map(st => (
        <TouchableOpacity key={st.label} style={s.statCard} onPress={() => navigateTo(st.screen)}>
          <View style={[s.statIcon, { backgroundColor: st.color + '22' }]}>
            <MaterialCommunityIcons name={st.icon} size={20} color={st.color} />
          </View>
          <Text style={[s.statVal, { color: st.color }]}>{st.val}</Text>
          <Text style={s.statLabel}>{st.label}</Text>
        </TouchableOpacity>
      ))}
    </View>
  );

  // ─── Render: Upcoming interview banner ────────────────────────────────────
  const renderInterviewBanner = () => upcomingInterview && (
    <TouchableOpacity style={s.interviewBanner} onPress={() => navigateTo('InterviewDetails', { interviewId: upcomingInterview.id })}>
      <View style={s.interviewBannerLeft}>
        <View style={s.interviewBannerIcon}>
          <MaterialCommunityIcons name="calendar-clock" size={22} color="#3B82F6" />
        </View>
        <View>
          <Text style={s.interviewBannerTitle}>Upcoming Interview</Text>
          <Text style={s.interviewBannerSub}>
            {upcomingInterview.interview_type ? `${upcomingInterview.interview_type} · ` : ''}
            {fmtDate(upcomingInterview.interview_date)}
          </Text>
        </View>
      </View>
      <View style={s.interviewBannerRight}>
        <Text style={s.interviewBannerCountdown}>
          {Math.max(0, Math.ceil((new Date(upcomingInterview.interview_date) - Date.now()) / 86400000))}d
        </Text>
        <Ionicons name="chevron-forward" size={16} color="#3B82F6" />
      </View>
    </TouchableOpacity>
  );

  // ─── Render: Quick actions ────────────────────────────────────────────────
  const renderQuickActions = () => (
    <View style={s.section}>
      <Text style={s.sectionTitle}>🚀 Quick Actions</Text>
      <View style={s.quickGrid}>
        {[
          { label:'Browse Jobs',       icon:'briefcase-search',        color:'#3B82F6', screen:'BrowseJobs'              },
          { label:'My Applications',   icon:'file-document-multiple',  color:'#10B981', screen:'JobApplications'         },
          { label:'Career Profile',    icon:'account-star',            color:'#F59E0B', screen:'Profile'                 },
          { label:'Build Resume',      icon:'file-account',            color:'#8B5CF6', screen:'BuildResume'             },
          { label:'Interview Prep',    icon:'chat-question',           color:'#EF4444', screen:'InterviewPrep'           },
          { label:'Job Alerts',        icon:'bell-ring-outline',       color:'#06B6D4', screen:'BrowseJobs',
            badge: alertCount > 0 ? alertCount : null },
          { label:'Career Coaching',   icon:'school',                  color:'#E85D04', screen:'CareerCoachingScreen'    },
          { label:'Dashboard',         icon:'view-dashboard',          color:'#6D28D9', screen:'ProfessionalDashboard'   },
        ].map((a, i) => (
          <TouchableOpacity key={i} style={s.quickCard} onPress={() => navigateTo(a.screen)}>
            <View style={[s.quickIcon, { backgroundColor: a.color + '18' }]}>
              <MaterialCommunityIcons name={a.icon} size={26} color={a.color} />
              {a.badge ? <View style={s.quickBadge}><Text style={s.quickBadgeText}>{a.badge}</Text></View> : null}
            </View>
            <Text style={s.quickLabel}>{a.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  // ─── Render: Profile completion tips ──────────────────────────────────────
  const renderCompletionCard = () => profileCompletion < 100 && (
    <TouchableOpacity style={s.completionCard} onPress={() => navigateTo('Profile')}>
      <View style={s.completionCardTop}>
        <MaterialCommunityIcons name="account-check-outline" size={22} color="#0e4e96" />
        <Text style={s.completionCardTitle}>Complete Your Profile</Text>
        <Text style={[s.completionCardPct, { color: scoreColor(profileCompletion) }]}>{profileCompletion}%</Text>
      </View>
      <View style={s.completionBar}>
        <View style={[s.completionBarFill, { width: `${profileCompletion}%`, backgroundColor: scoreColor(profileCompletion) }]} />
      </View>
      <Text style={s.completionCardSub}>
        {profileCompletion < 40  ? 'Complete basic info to get started — recruiters need to find you'
         : profileCompletion < 70 ? 'Add work experience & skills to appear in recruiter searches'
         : 'Add a profile photo and LinkedIn URL to unlock premium visibility'}
      </Text>
    </TouchableOpacity>
  );

  // ─── Render: Skill gap card ───────────────────────────────────────────────
  const renderSkillGap = () => missingSkills.length > 0 && (
    <TouchableOpacity style={s.skillGapCard} onPress={() => setSkillModal(true)}>
      <View style={s.skillGapHeader}>
        <MaterialCommunityIcons name="trending-up" size={20} color="#8B5CF6" />
        <Text style={s.skillGapTitle}>Recommended Skills to Add</Text>
        <Ionicons name="chevron-forward" size={16} color="#8B5CF6" />
      </View>
      <Text style={s.skillGapSub}>Boost your match score by adding these in-demand skills to your profile:</Text>
      <View style={s.skillGapChips}>
        {missingSkills.map((sk, i) => (
          <View key={i} style={s.skillGapChip}>
            <MaterialCommunityIcons name="plus-circle-outline" size={14} color="#8B5CF6" />
            <Text style={s.skillGapChipText}>{sk}</Text>
          </View>
        ))}
      </View>
    </TouchableOpacity>
  );

  // ─── Render: Recent applications ─────────────────────────────────────────
  const renderRecentApps = () => recentApplications.length > 0 && (
    <View style={s.section}>
      <View style={s.sectionRow}>
        <Text style={s.sectionTitle}>📋 Recent Applications</Text>
        <TouchableOpacity onPress={() => navigateTo('JobApplications')}>
          <Text style={s.seeAll}>See All →</Text>
        </TouchableOpacity>
      </View>
      {recentApplications.map((app, i) => {
        const statusColors = { active:'#3B82F6', accepted:'#10B981', rejected:'#EF4444', pending:'#F59E0B', withdrawn:'#9CA3AF' };
        const sc = statusColors[app.status] || '#9CA3AF';
        return (
          <TouchableOpacity key={i} style={s.appRow} onPress={() => navigateTo('ApplicationsScreen', { applicationId: app.id })}>
            <View style={[s.appDot, { backgroundColor: sc }]} />
            <View style={{ flex:1 }}>
              <Text style={s.appTitle} numberOfLines={1}>{app.job_title || app.current_title || 'Application'}</Text>
              <Text style={s.appMeta}>{app.candidate_location || 'UK'} · {fmtAgo(app.applied_date || app.created_at)}</Text>
            </View>
            <View style={[s.appStatus, { backgroundColor: sc + '22', borderColor: sc }]}>
              <Text style={[s.appStatusText, { color: sc }]}>{app.status || 'Applied'}</Text>
            </View>
          </TouchableOpacity>
        );
      })}
    </View>
  );

  // ─── Render: Category chips ───────────────────────────────────────────────
  const renderCategories = () => (
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.catBar} contentContainerStyle={{ paddingHorizontal:16, gap:8 }}>
      {JOB_CATEGORIES.map(c => (
        <TouchableOpacity key={c.id} style={[s.catChip, selCategory === c.id && s.catChipActive]} onPress={() => setSelCategory(c.id)}>
          <Ionicons name={c.icon} size={13} color={selCategory === c.id ? '#FFF' : '#64748b'} />
          <Text style={[s.catText, selCategory === c.id && s.catTextActive]}>{c.label}</Text>
        </TouchableOpacity>
      ))}
    </ScrollView>
  );

  // ─── Render: Featured jobs ────────────────────────────────────────────────
  const renderJobs = () => (
    <View style={s.section}>
      <View style={s.sectionRow}>
        <Text style={s.sectionTitle}>⭐ Featured Jobs</Text>
        <TouchableOpacity onPress={() => navigateTo('BrowseJobs')}>
          <Text style={s.seeAll}>See All ({filteredJobs.length}) →</Text>
        </TouchableOpacity>
      </View>

      {isLoading ? (
        <ActivityIndicator size={30} color="#0e4e96" style={{ marginVertical:20 }} />
      ) : filteredJobs.length === 0 ? (
        <View style={s.emptyJobs}>
          <MaterialCommunityIcons name="briefcase-off-outline" size={44} color="#CBD5E1" />
          <Text style={s.emptyJobsText}>No jobs found for "{searchQuery || selCategory}"</Text>
          <TouchableOpacity style={s.emptyJobsBtn} onPress={() => { setSearchQuery(''); setSelCategory('all'); }}>
            <Text style={s.emptyJobsBtnText}>Clear Filters</Text>
          </TouchableOpacity>
        </View>
      ) : (
        <FlatList
          data={filteredJobs.slice(0, 10)}
          keyExtractor={item => String(item.id || item._id || Math.random())}
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={{ paddingHorizontal:4 }}
          renderItem={({ item: job }) => {
            const isSaved = savedJobIds.has(String(job.id || job._id));
            const isUrgent = job.is_urgent || job.urgent;
            return (
              <TouchableOpacity style={s.jobCard} onPress={() => navigateTo('BrowseJobs', { selectedJob: job })}>
                {isUrgent && <View style={s.urgentBadge}><Text style={s.urgentText}>URGENT</Text></View>}

                <View style={s.jobCardTop}>
                  <View style={s.jobLogoBox}>
                    <MaterialCommunityIcons name="briefcase-outline" size={22} color="#0e4e96" />
                  </View>
                  <TouchableOpacity style={s.jobSaveBtn} onPress={() => toggleSaveJob(job)}>
                    <Ionicons name={isSaved ? 'bookmark' : 'bookmark-outline'} size={18} color={isSaved ? '#0e4e96' : '#94a3b8'} />
                  </TouchableOpacity>
                </View>

                <Text style={s.jobTitle} numberOfLines={2}>{job.title || 'Job Title'}</Text>
                <Text style={s.jobCompany} numberOfLines={1}>{job.company || job.department || 'Company'}</Text>

                <View style={s.jobMeta}>
                  <Ionicons name="location-outline" size={12} color="#94a3b8" />
                  <Text style={s.jobMetaText} numberOfLines={1}>{job.location || 'UK'}</Text>
                </View>
                <View style={s.jobMeta}>
                  <MaterialCommunityIcons name="currency-gbp" size={12} color="#94a3b8" />
                  <Text style={s.jobMetaText}>{fmtSalary(job.salary_min, job.salary_max)}</Text>
                </View>

                <View style={s.jobFooter}>
                  <View style={[s.jobTypeBadge, { backgroundColor: job.is_remote ? '#DCFCE7' : '#DBEAFE' }]}>
                    <Text style={[s.jobTypeBadgeText, { color: job.is_remote ? '#16A34A' : '#2563EB' }]}>
                      {job.is_remote ? 'Remote' : (job.job_type || 'Full-time')}
                    </Text>
                  </View>
                  <Text style={s.jobAge}>{fmtAgo(job.created_at || job.createdAt)}</Text>
                </View>

                <TouchableOpacity style={s.applyBtn} onPress={() => navigateTo('SubmitForJobs', { jobId: String(job.id), job })}>
                  <Text style={s.applyBtnText}>Quick Apply</Text>
                </TouchableOpacity>
              </TouchableOpacity>
            );
          }}
        />
      )}
    </View>
  );

  // ─── Render: Career tip rotator ───────────────────────────────────────────
  const renderCareerTip = () => {
    const tip = CAREER_TIPS[tipIndex];
    return (
      <View style={s.tipCard}>
        <View style={[s.tipIcon, { backgroundColor: tip.color + '22' }]}>
          <MaterialCommunityIcons name={tip.icon} size={22} color={tip.color} />
        </View>
        <View style={{ flex:1, marginLeft:12 }}>
          <Text style={s.tipTitle}>{tip.title}</Text>
          <Text style={s.tipText}>{tip.text}</Text>
        </View>
        <View style={s.tipDots}>
          {CAREER_TIPS.map((_, i) => (
            <TouchableOpacity key={i} onPress={() => setTipIndex(i)}>
              <View style={[s.tipDot, tipIndex === i && s.tipDotActive]} />
            </TouchableOpacity>
          ))}
        </View>
      </View>
    );
  };

  // ─── Render: Market insights ──────────────────────────────────────────────
  const renderMarketPreview = () => (
    <TouchableOpacity style={s.marketCard} onPress={() => setMarketModal(true)}>
      <View style={s.marketHeader}>
        <MaterialCommunityIcons name="chart-line" size={20} color="#0e4e96" />
        <Text style={s.marketTitle}>UK Market Insights</Text>
        <Text style={s.marketSeeAll}>View All →</Text>
      </View>
      <View style={s.marketPreviewRow}>
        {MARKET_INSIGHTS.slice(0, 3).map((m, i) => (
          <View key={i} style={s.marketPreviewItem}>
            <Text style={s.marketRole} numberOfLines={1}>{m.role.split(' ')[0]}</Text>
            <Text style={s.marketSalary}>{m.avgSalary}</Text>
            <Text style={[s.marketTrend, { color:'#10B981' }]}>{m.trend}</Text>
          </View>
        ))}
      </View>
    </TouchableOpacity>
  );

  // ─── Render: Resources links ──────────────────────────────────────────────
  const renderResources = () => (
    <View style={s.section}>
      <Text style={s.sectionTitle}>🛠 Professional Tools</Text>
      <View style={s.resourceGrid}>
        {[
          { label:'Build Resume',    icon:'file-account',       color:'#0e4e96', screen:'BuildResume'               },
          { label:'Upload Resume',   icon:'upload',             color:'#10B981', screen:'UploadResume'              },
          { label:'Career Coaching', icon:'school',             color:'#F59E0B', screen:'CareerCoachingScreen'      },
          { label:'Skill Progress',  icon:'chart-line',         color:'#8B5CF6', screen:'SkillProgress'             },
          { label:'Career Fairs',    icon:'calendar-star',      color:'#EF4444', screen:'CareerFairs'               },
          { label:'Interview Prep',  icon:'chat-question',      color:'#06B6D4', screen:'InterviewPrep'             },
        ].map((r, i) => (
          <TouchableOpacity key={i} style={s.resourceCard} onPress={() => navigateTo(r.screen)}>
            <MaterialCommunityIcons name={r.icon} size={22} color={r.color} />
            <Text style={s.resourceLabel}>{r.label}</Text>
          </TouchableOpacity>
        ))}
      </View>
    </View>
  );

  // ─── Modal: Market insights ───────────────────────────────────────────────
  const renderMarketModal = () => (
    <Modal visible={marketModal} animationType="slide" transparent onRequestClose={() => setMarketModal(false)}>
      <View style={s.modalOverlay}>
        <View style={s.modalSheet}>
          <View style={s.modalHeader}>
            <Text style={s.modalTitle}>UK Engineering Market</Text>
            <TouchableOpacity onPress={() => setMarketModal(false)}>
              <Ionicons name="close" size={24} color="#64748b" />
            </TouchableOpacity>
          </View>
          <ScrollView contentContainerStyle={{ padding:16, paddingBottom:40 }}>
            <Text style={s.modalSub}>Average UK engineering salaries and demand trends:</Text>
            {MARKET_INSIGHTS.map((m, i) => (
              <View key={i} style={s.marketRow}>
                <View style={{ flex:1 }}>
                  <Text style={s.marketRowRole}>{m.role}</Text>
                  <View style={[s.demandBadge, { backgroundColor: demandColor(m.demand) + '22' }]}>
                    <Text style={[s.demandText, { color: demandColor(m.demand) }]}>{m.demand} Demand</Text>
                  </View>
                </View>
                <View style={{ alignItems:'flex-end' }}>
                  <Text style={s.marketRowSalary}>{m.avgSalary}</Text>
                  <Text style={[s.marketRowTrend, { color:'#10B981' }]}>{m.trend} YoY</Text>
                </View>
              </View>
            ))}
          </ScrollView>
        </View>
      </View>
    </Modal>
  );

  // ─── Modal: Skill gap ─────────────────────────────────────────────────────
  const renderSkillModal = () => (
    <Modal visible={skillModal} animationType="slide" transparent onRequestClose={() => setSkillModal(false)}>
      <View style={s.modalOverlay}>
        <View style={[s.modalSheet, { maxHeight: 400 }]}>
          <View style={s.modalHeader}>
            <Text style={s.modalTitle}>Add These Skills</Text>
            <TouchableOpacity onPress={() => setSkillModal(false)}>
              <Ionicons name="close" size={24} color="#64748b" />
            </TouchableOpacity>
          </View>
          <View style={{ padding:16 }}>
            <Text style={s.modalSub}>These skills are in high demand for your industry. Add them to your profile to improve your match score:</Text>
            {missingSkills.map((sk, i) => (
              <View key={i} style={s.skillRow}>
                <MaterialCommunityIcons name="star-circle-outline" size={20} color="#8B5CF6" />
                <Text style={s.skillRowText}>{sk}</Text>
                <TouchableOpacity style={s.skillAddBtn} onPress={() => { setSkillModal(false); navigateTo('Profile', { addSkill: sk }); }}>
                  <Text style={s.skillAddBtnText}>Add to Profile</Text>
                </TouchableOpacity>
              </View>
            ))}
            <TouchableOpacity style={s.skillModalFooterBtn} onPress={() => { setSkillModal(false); navigateTo('Profile'); }}>
              <Text style={s.skillModalFooterBtnText}>Open My Profile</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // ─── Main render ──────────────────────────────────────────────────────────
  return (
    <SafeAreaView style={s.root}>
      <StatusBar barStyle="light-content" backgroundColor="#667eea" />

      {renderHeader()}
      {renderSearch()}

      <ScrollView
        style={s.scroll}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ paddingBottom: 100 }}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={['#0e4e96']} />}
      >
        {/* Hero section with gradient background */}
        <View style={s.heroBg}>
          {renderHero()}
          {renderInterviewBanner()}
        </View>

        {renderStats()}
        {renderCompletionCard()}
        {renderQuickActions()}
        {renderCategories()}
        {renderJobs()}
        {renderRecentApps()}
        {renderSkillGap()}
        {renderCareerTip()}
        {renderMarketPreview()}
        {renderResources()}
      </ScrollView>

      {/* FAB */}
      <TouchableOpacity style={s.fab} onPress={() => navigateTo('ProfessionalDashboard')}>
        <MaterialCommunityIcons name="view-dashboard" size={24} color="#FFF" />
      </TouchableOpacity>

      {renderMarketModal()}
      {renderSkillModal()}
    </SafeAreaView>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = StyleSheet.create({
  root:         { flex:1, backgroundColor:'#F8FAFC' },
  // Header
  header:       { flexDirection:'row', alignItems:'center', justifyContent:'space-between', backgroundColor:'#667eea', paddingHorizontal:14, paddingVertical:12, paddingTop: Platform.OS==='ios'?0:12 },
  headerLeft:   { flexDirection:'row', alignItems:'center', gap:10 },
  headerRight:  { flexDirection:'row', alignItems:'center', gap:8 },
  menuBtn:      { padding:6, borderRadius:16, backgroundColor:'rgba(255,255,255,0.18)' },
  logoRow:      { flexDirection:'row', alignItems:'center', gap:6 },
  logoText:     { fontSize:16, fontWeight:'bold', color:'#FFF' },
  iconBtn:      { position:'relative', padding:6 },
  notifBadge:   { position:'absolute', top:2, right:2, backgroundColor:'#EF4444', borderRadius:8, width:16, height:16, justifyContent:'center', alignItems:'center' },
  notifCount:   { fontSize:9, fontWeight:'bold', color:'#FFF' },
  avatarBtn:    { marginLeft:4 },
  avatarImg:    { width:34, height:34, borderRadius:17, borderWidth:2, borderColor:'rgba(255,255,255,0.6)' },
  avatarFallback:{ width:34, height:34, borderRadius:17, backgroundColor:'rgba(255,255,255,0.25)', justifyContent:'center', alignItems:'center' },
  avatarInitial:{ fontSize:15, fontWeight:'bold', color:'#FFF' },
  // Search
  searchBar:    { flexDirection:'row', alignItems:'center', backgroundColor:'#FFF', borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  searchInput:  { flex:1, height:44, fontSize:14, color:'#1e293b', paddingHorizontal:8 },
  // Hero
  heroBg:       { backgroundColor:'#667eea', paddingBottom:20 },
  heroCard:     { flexDirection:'row', alignItems:'flex-start', paddingHorizontal:18, paddingTop:18, paddingBottom:8 },
  heroLeft:     { flex:1 },
  greetText:    { fontSize:14, color:'rgba(255,255,255,0.8)', marginBottom:2 },
  heroName:     { fontSize:24, fontWeight:'900', color:'#FFF', marginBottom:2 },
  heroTitle:    { fontSize:14, color:'rgba(255,255,255,0.85)', fontWeight:'500', marginBottom:4 },
  heroLocRow:   { flexDirection:'row', alignItems:'center', gap:3, marginBottom:10 },
  heroLoc:      { fontSize:12, color:'rgba(255,255,255,0.7)' },
  premiumBadge: { flexDirection:'row', alignItems:'center', gap:5, backgroundColor:'rgba(255,215,0,0.2)', paddingHorizontal:12, paddingVertical:6, borderRadius:20, borderWidth:1, borderColor:'#FFD700', alignSelf:'flex-start' },
  premiumText:  { color:'#FFD700', fontWeight:'600', fontSize:12 },
  upgradeBtn:   { flexDirection:'row', alignItems:'center', gap:5, backgroundColor:'rgba(255,255,255,0.2)', paddingHorizontal:12, paddingVertical:8, borderRadius:20, alignSelf:'flex-start' },
  upgradeBtnText:{ color:'#FFF', fontWeight:'600', fontSize:12 },
  completionRing:{ alignItems:'center', gap:4 },
  ringOuter:    { width:72, height:72, borderRadius:36, borderWidth:4, justifyContent:'center', alignItems:'center', backgroundColor:'rgba(255,255,255,0.15)' },
  ringVal:      { fontSize:18, fontWeight:'bold' },
  ringLabel:    { fontSize:9, color:'rgba(255,255,255,0.7)' },
  ringHint:     { fontSize:10, color:'rgba(255,255,255,0.6)', textAlign:'center' },
  // Interview banner
  interviewBanner:{ flexDirection:'row', alignItems:'center', justifyContent:'space-between', backgroundColor:'rgba(255,255,255,0.18)', marginHorizontal:16, marginTop:8, borderRadius:12, padding:12, borderWidth:1, borderColor:'rgba(255,255,255,0.3)' },
  interviewBannerLeft:{ flexDirection:'row', alignItems:'center', gap:10 },
  interviewBannerIcon:{ width:36, height:36, borderRadius:18, backgroundColor:'rgba(255,255,255,0.9)', justifyContent:'center', alignItems:'center' },
  interviewBannerTitle:{ color:'#FFF', fontWeight:'700', fontSize:14 },
  interviewBannerSub:{ color:'rgba(255,255,255,0.8)', fontSize:12, marginTop:1 },
  interviewBannerRight:{ flexDirection:'row', alignItems:'center', gap:4 },
  interviewBannerCountdown:{ fontSize:20, fontWeight:'900', color:'#FFF' },
  // Stats row
  statsRow:     { flexDirection:'row', backgroundColor:'#FFF', paddingVertical:14, paddingHorizontal:8, borderBottomWidth:1, borderBottomColor:'#F1F5F9', gap:4 },
  statCard:     { flex:1, alignItems:'center', gap:4 },
  statIcon:     { width:36, height:36, borderRadius:18, justifyContent:'center', alignItems:'center' },
  statVal:      { fontSize:18, fontWeight:'bold' },
  statLabel:    { fontSize:9, color:'#9CA3AF', textAlign:'center' },
  // Quick actions
  section:      { paddingHorizontal:16, paddingTop:20 },
  sectionTitle: { fontSize:18, fontWeight:'700', color:'#1e293b', marginBottom:14 },
  sectionRow:   { flexDirection:'row', alignItems:'center', justifyContent:'space-between', marginBottom:14 },
  seeAll:       { fontSize:13, color:'#0e4e96', fontWeight:'600' },
  quickGrid:    { flexDirection:'row', flexWrap:'wrap', gap:10 },
  quickCard:    { alignItems:'center', width:(width-52)/4, gap:6 },
  quickIcon:    { position:'relative', width:52, height:52, borderRadius:26, justifyContent:'center', alignItems:'center' },
  quickBadge:   { position:'absolute', top:0, right:0, backgroundColor:'#EF4444', borderRadius:8, paddingHorizontal:4, paddingVertical:1 },
  quickBadgeText:{ fontSize:9, fontWeight:'bold', color:'#FFF' },
  quickLabel:   { fontSize:11, color:'#374151', textAlign:'center', fontWeight:'500' },
  // Profile completion
  completionCard:{ backgroundColor:'#EFF6FF', borderRadius:14, padding:14, marginHorizontal:16, marginTop:16, borderWidth:1, borderColor:'#BFDBFE' },
  completionCardTop:{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:8 },
  completionCardTitle:{ flex:1, fontSize:15, fontWeight:'700', color:'#1e40af' },
  completionCardPct:{ fontSize:18, fontWeight:'900' },
  completionBar:{ height:8, backgroundColor:'#DBEAFE', borderRadius:4, overflow:'hidden', marginBottom:8 },
  completionBarFill:{ height:8, borderRadius:4 },
  completionCardSub:{ fontSize:12, color:'#3730A3', lineHeight:18 },
  // Skill gap
  skillGapCard: { backgroundColor:'#F5F3FF', borderRadius:14, padding:14, marginHorizontal:16, marginTop:12, borderWidth:1, borderColor:'#DDD6FE' },
  skillGapHeader:{ flexDirection:'row', alignItems:'center', gap:8, marginBottom:6 },
  skillGapTitle:{ flex:1, fontSize:14, fontWeight:'700', color:'#6D28D9' },
  skillGapSub:  { fontSize:12, color:'#5B21B6', marginBottom:10, lineHeight:18 },
  skillGapChips:{ flexDirection:'row', flexWrap:'wrap', gap:6 },
  skillGapChip: { flexDirection:'row', alignItems:'center', gap:4, backgroundColor:'#EDE9FE', borderRadius:16, paddingHorizontal:10, paddingVertical:5 },
  skillGapChipText:{ fontSize:12, color:'#6D28D9', fontWeight:'600' },
  // Recent applications
  appRow:       { flexDirection:'row', alignItems:'center', gap:10, backgroundColor:'#FFF', borderRadius:10, padding:12, marginBottom:8, borderWidth:1, borderColor:'#F1F5F9' },
  appDot:       { width:10, height:10, borderRadius:5 },
  appTitle:     { fontSize:14, fontWeight:'600', color:'#1e293b' },
  appMeta:      { fontSize:11, color:'#94a3b8', marginTop:2 },
  appStatus:    { borderRadius:10, borderWidth:1, paddingHorizontal:8, paddingVertical:3 },
  appStatusText:{ fontSize:10, fontWeight:'700' },
  // Categories
  catBar:       { paddingVertical:10, flexGrow:0 },
  catChip:      { flexDirection:'row', alignItems:'center', gap:4, paddingHorizontal:12, paddingVertical:7, borderRadius:20, backgroundColor:'#F1F5F9' },
  catChipActive:{ backgroundColor:'#0e4e96' },
  catText:      { fontSize:12, color:'#64748b', fontWeight:'500' },
  catTextActive:{ color:'#FFF' },
  // Jobs
  emptyJobs:    { alignItems:'center', paddingVertical:40 },
  emptyJobsText:{ fontSize:14, color:'#9CA3AF', marginTop:10, textAlign:'center' },
  emptyJobsBtn: { marginTop:14, backgroundColor:'#0e4e96', borderRadius:10, paddingHorizontal:20, paddingVertical:10 },
  emptyJobsBtnText:{ color:'#FFF', fontWeight:'600', fontSize:13 },
  jobCard:      { width:200, backgroundColor:'#FFF', borderRadius:14, padding:14, marginRight:12, shadowColor:'#000', shadowOffset:{width:0,height:2}, shadowOpacity:0.07, shadowRadius:6, elevation:3 },
  jobCardTop:   { flexDirection:'row', justifyContent:'space-between', alignItems:'center', marginBottom:8 },
  jobLogoBox:   { width:40, height:40, borderRadius:10, backgroundColor:'#EFF6FF', justifyContent:'center', alignItems:'center' },
  jobSaveBtn:   { padding:4 },
  urgentBadge:  { position:'absolute', top:8, left:8, backgroundColor:'#EF4444', borderRadius:8, paddingHorizontal:6, paddingVertical:2, zIndex:1 },
  urgentText:   { fontSize:9, fontWeight:'bold', color:'#FFF' },
  jobTitle:     { fontSize:14, fontWeight:'700', color:'#1e293b', marginBottom:3 },
  jobCompany:   { fontSize:12, color:'#0e4e96', fontWeight:'600', marginBottom:6 },
  jobMeta:      { flexDirection:'row', alignItems:'center', gap:4, marginBottom:3 },
  jobMetaText:  { fontSize:11, color:'#64748b' },
  jobFooter:    { flexDirection:'row', alignItems:'center', justifyContent:'space-between', marginTop:6, marginBottom:10 },
  jobTypeBadge: { borderRadius:10, paddingHorizontal:7, paddingVertical:3 },
  jobTypeBadgeText:{ fontSize:10, fontWeight:'600' },
  jobAge:       { fontSize:10, color:'#94a3b8' },
  applyBtn:     { backgroundColor:'#0e4e96', borderRadius:10, paddingVertical:8, alignItems:'center' },
  applyBtnText: { color:'#FFF', fontSize:12, fontWeight:'700' },
  // Career tips
  tipCard:      { flexDirection:'row', alignItems:'flex-start', backgroundColor:'#FFF', borderRadius:14, padding:16, marginHorizontal:16, marginTop:20, borderWidth:1, borderColor:'#F1F5F9' },
  tipIcon:      { width:44, height:44, borderRadius:22, justifyContent:'center', alignItems:'center', flexShrink:0 },
  tipTitle:     { fontSize:14, fontWeight:'700', color:'#1e293b', marginBottom:4 },
  tipText:      { fontSize:12, color:'#64748b', lineHeight:18 },
  tipDots:      { flexDirection:'row', gap:4, marginTop:8, alignSelf:'flex-end' },
  tipDot:       { width:6, height:6, borderRadius:3, backgroundColor:'#E2E8F0' },
  tipDotActive: { backgroundColor:'#0e4e96' },
  // Market
  marketCard:   { backgroundColor:'#FFF', borderRadius:14, padding:16, marginHorizontal:16, marginTop:16, borderWidth:1, borderColor:'#F1F5F9' },
  marketHeader: { flexDirection:'row', alignItems:'center', gap:8, marginBottom:14 },
  marketTitle:  { flex:1, fontSize:15, fontWeight:'700', color:'#1e293b' },
  marketSeeAll: { fontSize:12, color:'#0e4e96', fontWeight:'600' },
  marketPreviewRow:{ flexDirection:'row', justifyContent:'space-around' },
  marketPreviewItem:{ alignItems:'center', gap:2 },
  marketRole:   { fontSize:11, color:'#64748b' },
  marketSalary: { fontSize:15, fontWeight:'800', color:'#0e4e96' },
  marketTrend:  { fontSize:11, fontWeight:'700' },
  // Resources
  resourceGrid: { flexDirection:'row', flexWrap:'wrap', gap:10 },
  resourceCard: { width:(width-52)/3, backgroundColor:'#FFF', borderRadius:12, padding:14, alignItems:'center', gap:8, borderWidth:1, borderColor:'#F1F5F9' },
  resourceLabel:{ fontSize:11, color:'#374151', textAlign:'center', fontWeight:'500' },
  // FAB
  fab:          { position:'absolute', bottom:24, right:20, width:54, height:54, borderRadius:27, backgroundColor:'#0e4e96', justifyContent:'center', alignItems:'center', shadowColor:'#0e4e96', shadowOffset:{width:0,height:4}, shadowOpacity:0.35, shadowRadius:8, elevation:8 },
  // Modals
  modalOverlay: { flex:1, backgroundColor:'rgba(0,0,0,0.5)', justifyContent:'flex-end' },
  modalSheet:   { backgroundColor:'#FFF', borderTopLeftRadius:24, borderTopRightRadius:24, overflow:'hidden' },
  modalHeader:  { flexDirection:'row', justifyContent:'space-between', alignItems:'center', padding:20, borderBottomWidth:1, borderBottomColor:'#E2E8F0' },
  modalTitle:   { fontSize:18, fontWeight:'700', color:'#1e293b' },
  modalSub:     { fontSize:13, color:'#64748b', marginBottom:16, lineHeight:20 },
  marketRow:    { flexDirection:'row', alignItems:'center', backgroundColor:'#F8FAFC', borderRadius:10, padding:14, marginBottom:10 },
  marketRowRole:{ fontSize:14, fontWeight:'700', color:'#1e293b', marginBottom:4 },
  demandBadge:  { borderRadius:10, paddingHorizontal:8, paddingVertical:3, alignSelf:'flex-start' },
  demandText:   { fontSize:10, fontWeight:'700' },
  marketRowSalary:{ fontSize:18, fontWeight:'900', color:'#0e4e96' },
  marketRowTrend:{ fontSize:12, fontWeight:'700', textAlign:'right' },
  skillRow:     { flexDirection:'row', alignItems:'center', gap:10, paddingVertical:10, borderBottomWidth:1, borderBottomColor:'#F1F5F9' },
  skillRowText: { flex:1, fontSize:14, color:'#1e293b', fontWeight:'600' },
  skillAddBtn:  { backgroundColor:'#8B5CF6', borderRadius:10, paddingHorizontal:12, paddingVertical:6 },
  skillAddBtnText:{ color:'#FFF', fontSize:12, fontWeight:'600' },
  skillModalFooterBtn:{ backgroundColor:'#0e4e96', borderRadius:12, padding:14, alignItems:'center', marginTop:16 },
  skillModalFooterBtnText:{ color:'#FFF', fontWeight:'700', fontSize:15 },
});
